import type { GPLocaleObject } from '~/i18n/types';
import { USER_CURRENCY_STORE_KEY } from '~/store/currency';

// Injects an onMounted hook which updates the currency in the vue store after
// loading the pages on client side.
// The currency can be overridden by the locale store or the browser for special
// cases like en-GB.
export default (
  onloadCurrencySetHandler: () => void = () => undefined,
  userCurrencySetHandler: () => void = () => undefined,
) => {
  const { locale, localeProperties } = useI18n();
  const { supportedCurrencies } = useAppConfig();
  const authStore = useAuthStore();
  const currencyStore = useCurrencyStore();

  watch(
    () => currencyStore.currency,
    () => {
      if (currencyStore.preferUserDecision) {
        userCurrencySetHandler();
      }
    },
  );

  onNuxtReady(async () => {
    if (import.meta.client) {
      // We have a user override in the local store
      const userCurrency = localStorage.getItem(
        USER_CURRENCY_STORE_KEY,
      ) as string;
      if (userCurrency) {
        // An authenticated customer's wallet currency is authoritative: it is what their
        // balance is denominated in and what prices are quoted in for their active region.
        // A stored override that disagrees with it is stale — it was chosen against a
        // different region or account. useRegionSwitch only clears the key on its own
        // switch path, so any other route to a different region (fresh login, region
        // resolved from the webinterface cookie, a full reload after the region changed)
        // left the old value behind, and this hook then reapplied it on every load —
        // SSR rendered the correct region's price and the client immediately flipped it
        // back. That is the TV-1808 symptom; drop the stale key instead.
        const walletCurrency =
          authStore.activeCustomer?.wallet?.currencyCode ?? null;

        if (walletCurrency != null && walletCurrency !== userCurrency) {
          localStorage.removeItem(USER_CURRENCY_STORE_KEY);
        } else if (
          // The user currency is supported
          supportedCurrencies.includes(userCurrency) &&
          // The user currency is not the same as the default locale currency
          // For example, if the user preference us EUR and the default locale is de, so we do not need to change the currency
          (localeProperties.value as GPLocaleObject).currency !== userCurrency
        ) {
          // The reason for the second fetch() is a little tricky: The fetch() is running,
          // and the currency will change before the fetch() returned. So the fetch()
          // collides with the code below. To prevent this, we call fetch() again to make
          // sure we have the right currency.

          currencyStore.setCurrency(userCurrency, true);
          onloadCurrencySetHandler();
        }

        // Return either way. When the override was stale the wallet currency is already in
        // the store (applied during auth bootstrap / SSR), so leaving it untouched is the
        // fix — falling through to the anonymous GB branch below would undo it.
        return;
      }
      // GB is a special case here, because it is mapped to "en", but uses a different
      // currency. For that, we check the Accept-Language header for en-GB.
      // This branch is client-only because it is the *anonymous* path: those responses
      // are cacheable, so the render must not vary on a cookie, localStorage is not
      // readable on the server, and GB cannot be derived from the /en/ path prefix.
      // This is NOT a general "no cookies / no auth on the server" rule — authenticated
      // SSR is allowed and preferred (requests carrying gp_at bypass the nginx cache).
      // Resolve authenticated state server-side wherever possible; deferring it to the
      // client causes flickering loads and hydration mismatches. See the SSR auth
      // bootstrap in ~/plugins/auth.keycloak.server.ts.
      // We check if ...
      //   - no customer with a valid wallet is active
      //   - the browser Accept-Language is set to GB (so we have a special case here)
      //   - the language is EN (we enter the /en/ cache file)
      //   - and the user did not change the language by themselves (user did not override
      //     that special case)
      if (
        // XXX: Add more special cases for Malta and Ireland later on
        !authStore.isAuthenticated &&
        navigator.language.startsWith('en-GB') &&
        locale.value === 'en' &&
        !currencyStore.preferUserDecision
      ) {
        currencyStore.setCurrency('GBP', false);
        await onloadCurrencySetHandler();
      }
    }
  });
};
